import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import axios from 'axios';
import { FaCrown } from 'react-icons/fa';
import { getEmptyImage } from 'react-dnd-html5-backend';
import './EarnSection.css';

const CharacterCard = ({ character, index, moveCard, baseURL, isGameOver }) => {
    const [{ isDragging }, drag, preview] = useDrag({
      type: 'CHARACTER',
      item: { id: character.id, index },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
      canDrag: !isGameOver, // Disable dragging if game is over
    });
  
    const [, drop] = useDrop({
      accept: 'CHARACTER',
      hover: (draggedItem) => {
        if (draggedItem.index !== index) {
          moveCard(draggedItem.index, index);
          draggedItem.index = index;
        }
      },
      canDrop: () => !isGameOver, // Disable dropping if game is over
    });
    // Reference for the component to set custom drag preview
    const dragPreviewRef = useRef(null);
  
    useEffect(() => {
      // Hide the default drag preview
      preview(getEmptyImage(), { captureDraggingState: true });
      // Optionally, you can set custom drag previews here if needed
    }, [preview]);
  
    const imageUrl = `${baseURL}${character.image_url}`;
    const profileUrl = `https://onepiece.fandom.com/wiki/${character.profile_suffix}`;
  
    return (
      <div
        ref={(node) => drag(drop(node))}
        className={`character-card ${character.isCorrect ? 'correct' : ''} ${isDragging ? 'dragging' : ''}`}
        style={{
          opacity: isDragging ? 0.5 : 1,
          cursor: isGameOver ? 'default' : 'grab', // Set the cursor to default if the game is over
        }}
      >
        <div className="character-card-content">
          <div className="character-image">
            <img src={imageUrl} alt={character.name} draggable="false"/>
          </div>
          <div className="character-info">
            <h3 className="character-name">
              <a href={profileUrl} target="_blank" rel="noopener noreferrer">
                {character.name}
              </a>
            </h3>
            {isGameOver && <p className="character-rank">Rank: {character.rank}</p>} {/* Display rank only when game is over */}
            <p className = "character-profile">
              <a href={profileUrl} target="_blank" rel="noopener noreferrer">
                View Profile
              </a>
            </p>
          </div>
        </div>
  
        {/* Render the crown icon only for the first character */}
        {index === 0 && (
          <div className="crown-icon">
            <FaCrown size={24} color="#ffd700" />
          </div>
        )}
      </div>
    );
  };

  const fetchCharacters = async (setCharacters, setError, setSortedCharacters, setApiGameOverStatus, setTries, setTimeLeft, setCircleStatus, refreshTokenIfNeeded, loggedInUser) => {
    try {
      const response = await axios.post('/api/random-characters/');
      processApiResponse(response, setCharacters, setSortedCharacters, setApiGameOverStatus, setTries, setTimeLeft, setCircleStatus, loggedInUser);
    } catch (error) {
      console.error('Error fetching characters:', error);

      if (error.response && error.response.status === 401) {
        try {
          console.log('Refreshing token...');
          await refreshTokenIfNeeded();
  
          // Retry after token refresh
          const response = await axios.post('/api/random-characters/');
          processApiResponse(response, setCharacters, setSortedCharacters, setApiGameOverStatus, setTries, setTimeLeft, setCircleStatus, loggedInUser);
        } catch (retryError) {
          console.error('Retry failed after token refresh:', retryError);
          fetchGuestCharacters(setCharacters, setError, setSortedCharacters, setTimeLeft);
        }
      } else {
        setError('Failed to load characters. Please try again later.');
        fetchGuestCharacters(setCharacters, setError, setSortedCharacters, setTimeLeft);
      }
    }
  };

  const fetchGuestCharacters = async(setCharacters, setError, setSortedCharacters, setTimeLeft) => {
    try {
      const response = await axios.get('/api/random-characters/guest/')
      processGuestApiResponse(response, setCharacters, setSortedCharacters, setTimeLeft);
    } catch (error) {
      console.error('Error fetching characters:', error);
      setError('Failed to load characters. Please try again later.');
    }
  }
  
  const hashUser = (user) => {
    let hash = 0;
    for (let i = 0; i < user.length; i++) {
      const char = user.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return hash.toString();
  };
  
  const getStorageWithExpiry = (key) => {
    const itemStr = localStorage.getItem(key);
    if (!itemStr) return null;
  
    const item = JSON.parse(itemStr);
    const now = new Date().getTime();
    
    // Check if the item has expired
    if (now > item.timestamp + item.expiresIn) {
      localStorage.removeItem(key);
      return null;
    }
    return item.value;
  };

  const processApiResponse = (response, setCharacters, setSortedCharacters, setApiGameOverStatus, setTries, setTimeLeft, setCircleStatus, loggedInUser) => {
    const randomCharacters = response.data.characters;
    const gameStatus = response.data.user_state.state;
    const attempt = response.data.user_state.attempt;
    const timeUntilNextDay = response.data.time_until_next_day;

    setApiGameOverStatus(gameStatus);
    setTries(attempt);
    setTimeLeft(timeUntilNextDay * 1000);
    
    // Update the circle status

    const updatedCircleStatus = updateCircleStatus(gameStatus, attempt);

    setCircleStatus(updatedCircleStatus);      

    // Add 'wasEverCorrect' and 'isCorrect' fields to each character
    const charactersWithCorrectStatus = randomCharacters.map((character) => ({
      ...character,
      wasEverCorrect: false, // Initially, no character is correct
      isCorrect: false, // Initially, all characters are incorrect
    }));

    if (gameStatus === 'pass') {
      // Update all characters to be correct
      charactersWithCorrectStatus.forEach((character) => {
        character.wasEverCorrect = true;
        character.isCorrect = true;
      });
    }

    // Sort characters by rank and save the sorted order
    const sortedByRank = [...charactersWithCorrectStatus].sort((a, b) => a.rank - b.rank);
    setSortedCharacters(sortedByRank);

    if (loggedInUser && loggedInUser !== '') {
      const storageKey = `characters_${hashUser(loggedInUser)}`;
      const savedData = getStorageWithExpiry(storageKey);
      
      if (gameStatus === 'pass') {
        // If game is passed, remove saved data and set sorted characters
        if (savedData) {
          localStorage.removeItem(storageKey);
        }
        setCharacters(sortedByRank);
      } else {
        if (savedData) {
          // Check if saved characters match current randomCharacters (in any order)
          const savedCharacterNames = new Set(savedData.map(char => char.name));
          const currentCharacterNames = new Set(randomCharacters.map(char => char.name));
          
          const charactersMatch = savedCharacterNames.size === currentCharacterNames.size && 
            [...savedCharacterNames].every(name => currentCharacterNames.has(name));
  
          if (charactersMatch) {
            // Create new array maintaining saved order but using current character data
            const orderedCharacters = savedData.map(savedChar => {
              const currentChar = charactersWithCorrectStatus.find(c => c.name === savedChar.name);
              return {
                ...currentChar, // Keep all current character data
                wasEverCorrect: savedChar.wasEverCorrect,
                isCorrect: savedChar.isCorrect
              };
            });
            setCharacters(orderedCharacters);
          } else {
            // If characters don't match, delete saved data and use new random characters
            localStorage.removeItem(storageKey);
            setCharacters(charactersWithCorrectStatus);
          }
        } else {
          // If no saved data exists, use new random characters
          setCharacters(charactersWithCorrectStatus);
        }
      }
    } else {
      // If no logged in user, just set characters based on game status
      if (gameStatus === 'pass') {
        setCharacters(sortedByRank);
      } else {
        setCharacters(charactersWithCorrectStatus);
      }
    }
  }

  const updateCircleStatus = (gameStatus, attempt) => {
    const updatedCircleStatus = [null, null, null]; // Initialize the array

        if (gameStatus === 'fail') 
        {
          updatedCircleStatus[0] = 'red';
          updatedCircleStatus[1] = 'red';
          updatedCircleStatus[2] = 'red';
        } 
        else if (gameStatus === 'pass') 
        {
          let attemptIndex = attempt - 1;
          updatedCircleStatus[attemptIndex] = 'green';

          while (attemptIndex - 1 >= 0) 
          {
            attemptIndex -= 1;
            updatedCircleStatus[attemptIndex] = 'red';
          }
        } 
        else if (gameStatus === 'none') 
        {
          let attemptIndex = attempt - 1;

          if (attemptIndex >= 0) 
          {
            updatedCircleStatus[attemptIndex] = 'red';
          }
          while (attemptIndex - 1 >= 0) 
          {
            attemptIndex -= 1;
            updatedCircleStatus[attemptIndex] = 'red';
          }
        }

        return updatedCircleStatus;
    }

    const processGuestApiResponse = (response, setCharacters, setSortedCharacters, setTimeLeft) => {
      const randomCharacters = response.data.characters;
      const timeUntilNextDay = response.data.time_until_next_day;
  
      setTimeLeft(timeUntilNextDay * 1000);
      
      // Add 'wasEverCorrect' and 'isCorrect' fields to each character
      const charactersWithCorrectStatus = randomCharacters.map((character) => ({
        ...character,
        wasEverCorrect: false, // Initially, no character is correct
        isCorrect: false, // Initially, all characters are incorrect
      }));
  
      // Sort characters by rank and save the sorted order
      const sortedByRank = [...charactersWithCorrectStatus].sort((a, b) => a.rank - b.rank);
      setSortedCharacters(sortedByRank);
  
      // Save the random order separately
      setCharacters(charactersWithCorrectStatus);
    }

  const EarnSection = ({ setNotification, refreshTokenIfNeeded, loggedInUser }) => {
    const [characters, setCharacters] = useState([]);
    const [sortedCharacters, setSortedCharacters] = useState([]);
    const [error, setError] = useState(null);
    const [tries, setTries] = useState(0);
    const [maxTries, setMaxTries] = useState(3);
    const [lastSubmittedOrder, setLastSubmittedOrder] = useState([]);
    const [circleStatus, setCircleStatus] = useState([null, null, null]);
    const [isPopUpVisible, setIsPopUpVisible] = useState(false);
    const [isCorrectOrder, setIsCorrectOrder] = useState(false);
    const [timeLeft, setTimeLeft] = useState('');
    const [apiGameOverStatus, setApiGameOverStatus] = useState("none"); // Can be 'pass', 'fail', or 'none'
    const baseURL = 'https://images.gomugomuguess.com/one-piece-character-images/';
  
    const isGameOver = apiGameOverStatus !== "none" || characters.every((character) => character.wasEverCorrect) || tries >= maxTries;
  
    // Function to format milliseconds to HH:MM:SS
    const formatTime = (milliseconds) => {
      const totalSeconds = Math.floor(milliseconds / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
  
      const pad = (num) => num.toString().padStart(2, '0');
      return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
    };
  
    // Update countdown timer for isGameOver condition and background countdown
    useEffect(() => {
      let intervalId;
      
      // Ensure timeLeft does not go below 30000 if the game is not over
      if (!isGameOver && timeLeft < 30000) {
        setTimeLeft(30000); // Set timeLeft to 30 seconds
      }

      if (!isGameOver && timeLeft > 30000) { // Start countdown while game is ongoing and more than 30 seconds left
        const updateTimer = () => {
          setTimeLeft((prevTime) => prevTime - 1000); // Decrease by 1 second

          if (timeLeft <= 30000) { // Stop background countdown once it hits 30 seconds
            clearInterval(intervalId);
          }
        };

        intervalId = setInterval(updateTimer, 1000);
      } else if (isGameOver && timeLeft > 0) { // Keep countdown logic for game over
        const updateTimer = () => {
          setTimeLeft((prevTime) => {
            if (prevTime <= 1000) {
              clearInterval(intervalId);
              // Call reset function
              handleSoftReset();
              return 0;
            }
            return prevTime - 1000;
          });
        };

        intervalId = setInterval(updateTimer, 1000);
      }

      return () => {
        if (intervalId) {
          clearInterval(intervalId);
        }
      };
    }, [isGameOver, timeLeft]);
  
    const handleSoftReset = () => {
      // Reset game states to their initial values
      setTries(0); // Reset attempts
      setCircleStatus([null, null, null]); // Reset the attempt circles
      setIsPopUpVisible(false); // Hide the popup
      setIsCorrectOrder(false); // Reset correct order flag
      setTimeLeft(''); // Reset time left
      setLastSubmittedOrder([]); // Clear the last submitted order
      setApiGameOverStatus('none');
      
      if (loggedInUser && loggedInUser !== '')
      {
        // Fetch new characters or reset characters state as needed
        fetchCharacters(setCharacters, setError, setSortedCharacters, setApiGameOverStatus, setTries, setTimeLeft, setCircleStatus, refreshTokenIfNeeded, loggedInUser);
      }
      else if (loggedInUser === '')
      {
        fetchGuestCharacters(setCharacters, setError, setSortedCharacters, setTimeLeft);
      }
      else
      {
        // Do nothing
      }
    };

    useEffect(() => { 
      if (loggedInUser && loggedInUser !== '') {
        handleSoftReset();
      }
      else if (loggedInUser === '')
      {
        handleSoftReset();
      }
      else
      {
        // Do nothing
      }
    }, [loggedInUser]);
  
    const moveCard = useCallback((dragIndex, hoverIndex) => {
      setCharacters((prevCards) => {
        const newCards = [...prevCards];
        const draggedCard = newCards[dragIndex];
        newCards.splice(dragIndex, 1);
        newCards.splice(hoverIndex, 0, draggedCard);
  
        return newCards.map((character, index) => {
          const correctPosition = sortedCharacters.findIndex(
            (sortedCharacter) => sortedCharacter.id === character.id
          );
          const isCorrect = character.wasEverCorrect && index === correctPosition;
          return { ...character, isCorrect };
        });
      });
    }, [sortedCharacters]);
    
    const EXPIRATION_HOURS = 24;
    
    const stripCardData = (cards) => {
      return cards.map(card => {
        const { id, image_url, rank, profile_suffix, ...strippedCard } = card;
        return strippedCard;
      });
    };

    const setStorageWithExpiry = (key, value) => {
      const item = {
        value: stripCardData(value),
        timestamp: new Date().getTime(),
        expiresIn: EXPIRATION_HOURS * 60 * 60 * 1000 // 24 hours in milliseconds
      };
      localStorage.setItem(key, JSON.stringify(item));
    };
    
    const checkAnswers = async () => {
      try {
        const currentOrder = characters.map(c => c.id);
        const lastOrder = lastSubmittedOrder.map(c => c.id);

        // If the order hasn't changed, we return early
        if (JSON.stringify(currentOrder) === JSON.stringify(lastOrder)) {
          return;
        }

        if (isGameOver) {
          return;
        }
      
        if (tries < maxTries) {
          const newTryCount = tries + 1;
          setTries(newTryCount);
          setLastSubmittedOrder(characters);
          
          let updatedCardsToSave;
    
          // Update character states based on correctness
          setCharacters((prevCards) => {
            const updatedCards = prevCards.map((character, index) => {
              const correctPosition = sortedCharacters.findIndex(
                (sortedCharacter) => sortedCharacter.id === character.id
              );
              const isInCorrectPosition = index === correctPosition;
      
              return {
                ...character,
                wasEverCorrect: character.wasEverCorrect || isInCorrectPosition,
                isCorrect: isInCorrectPosition
              };
            });
      
            const allCorrect = updatedCards.every((character, index) => {
              const correctPosition = sortedCharacters.findIndex(
                (sortedCharacter) => sortedCharacter.id === character.id
              );
              return index === correctPosition;
            });
      
            setIsCorrectOrder(allCorrect);
      
            // If the user has completed the max attempts or is correct, show the popup
            if (newTryCount >= maxTries || allCorrect) {
              setIsPopUpVisible(true);
            }
            
            updatedCardsToSave = updatedCards;
    
            return updatedCards;
          });
      
          // Update the circle status
          const updatedCircleStatus = [...circleStatus];
          updatedCircleStatus[tries] = isOrderCorrect(currentOrder) ? 'green' : 'red';
          setCircleStatus(updatedCircleStatus);
      
          // Determine the state to be passed in the PATCH request
          let state;
          if (isOrderCorrect(currentOrder)) {
            state = 'pass';
          } else {
            state = newTryCount === maxTries ? 'fail' : 'none';
          }
          
          if (loggedInUser && loggedInUser !== '') {
            // Hash the loggedInUser to use as the localStorage key
            const storageKey = `characters_${hashUser(loggedInUser)}`;
            
            // Send PATCH request to update user state on the backend
            try {
              await axios.patch(`/api/update-user-state/`,
                {
                  state: state,
                  attempt: newTryCount,
                });          
              
              // Save characters using the hashed user key with expiration
              setStorageWithExpiry(storageKey, updatedCardsToSave);
              
            } catch (error) {
              console.error('Error updating user state:', error);
              if (error.response && error.response.status === 401) {
                try {
                  console.log('Refreshing token...');
                  await refreshTokenIfNeeded();
          
                  // Retry PATCH request
                  console.log('Retrying PATCH request after token refresh...');
                  await axios.patch(`/api/update-user-state/`,
                    {
                      state: state,
                      attempt: newTryCount,
                    });       
                  
                  // Save characters using the hashed user key with expiration after retry
                  setStorageWithExpiry(storageKey, updatedCardsToSave);
                  
                } catch (retryError) {
                  console.error('Retry failed after token refresh:', retryError);
                }
              }
            }

            if (state === 'pass')
            {
              try {
                // Call the backend API to increase the balance
                const response = await axios.patch('/api/increase-balance/'); 
        
              } catch (error) {
                console.error('Error increasing balance:', error);
                if (error.response && error.response.status === 401) {
                  try {
                    console.log('Refreshing token...');
                    await refreshTokenIfNeeded();
            
                    // Retry PATCH request
                    console.log('Retrying increase balance request after token refresh...');
                    const response = await axios.patch('/api/increase-balance/'); 
                  } catch (retryError) {
                    console.error('Retry failed after token refresh:', retryError);
                  }
                }
              }
            }
          }
        }
      } catch (error) {
        console.error('Error during checkAnswers:', error);
      }
    };   
  
    const isOrderCorrect = (currentOrder) => {
      const correctOrder = sortedCharacters.map((char) => char.id);
      return JSON.stringify(currentOrder) === JSON.stringify(correctOrder);
    };
    
    const handleCollect = () => {
      setIsPopUpVisible(false);
      setNotification(true);
    };

    return (
      <div className="earn-section">
        <h1><a href="https://onepiece.fandom.com/wiki/World_Economy_News_Paper" target="_blank" rel="noopener noreferrer">One Piece Power Rankings</a></h1>
        <h3>"Play To Earn Berries!" – <a href="https://onepiece.fandom.com/wiki/Morgans" target="_blank" rel="noopener noreferrer">"Big News" Morgans</a></h3>
        {error && <div className="error-message">{error}</div>}
        <div className="character-list">
          {characters.length === 0 ? (
            <p></p>
          ) : (
            characters.map((character, index) => (
              <CharacterCard
                key={character.id}
                character={character}
                index={index}
                moveCard={moveCard}
                baseURL={baseURL}
                isGameOver={isGameOver}
              />
            ))
          )}
        </div>
        {characters.length > 0 && (
        <>
        <div className="tries-info">
          <div className="tries-circles">
            {circleStatus.map((status, index) => (
              <div
                key={index}
                className={`try-circle ${status ? status : ''}`}
              ></div>
            ))}
          </div>
        </div>
        <button 
          className="check-answers-btn" 
          onClick={checkAnswers}
        >
          {isGameOver ? formatTime(timeLeft) : 'Check Answers'}
        </button>
        </>
        )}
        {isPopUpVisible && (
          <div className="popup-overlay">
            <div className="popup">
              {isCorrectOrder ? (
                <>
                  <h2>Congratulations!</h2>
                  <p>You correctly ordered the characters.</p>
                  <p>Reward: <a href="https://onepiece.fandom.com/wiki/Belly" target="_blank" rel="noopener noreferrer">B$500</a></p>
                  <button onClick={handleCollect}>Collect</button>
                </>
              ) : (
                <>
                  <h2>Too bad!</h2>
                  <p>You didn't order the characters correctly.</p>
                  <p>Try again tomorrow.</p>
                  <button onClick={() => setIsPopUpVisible(false)}>Close</button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

export default EarnSection;
